import { createContext, useContext, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const StoreContext = createContext(null);

export function StoreProvider({ children }) {
  const [user, setUser] = useState(undefined);
  const [cart, setCart] = useState(() => JSON.parse(localStorage.getItem("savyron_cart") || "[]"));
  const [wishlist, setWishlist] = useState(() => JSON.parse(localStorage.getItem("savyron_wishlist") || "[]"));
  const [settings, setSettings] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [cartPing, setCartPing] = useState(0);

  useEffect(() => { localStorage.setItem("savyron_cart", JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem("savyron_wishlist", JSON.stringify(wishlist)); }, [wishlist]);

  useEffect(() => {
    // CRITICAL: skip the /me check when returning from Google OAuth — AuthCallback exchanges the session first
    if (!window.location.hash?.includes("session_id=")) {
      api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null));
    }
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => setSettings({ gift_wrap_price: 99 }));
  }, []);

  const saveToken = (token) => localStorage.setItem("savyron_token", token);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    saveToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    saveToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("savyron_token");
    setUser(null);
  };

  const addToCart = (product, qty = 1, personalization = null) => {
    setCart((prev) => {
      const key = personalization ? `${product.slug}::${personalization.name}::${personalization.photo || ""}` : product.slug;
      const ex = prev.find((i) => (i.key || i.slug) === key);
      if (ex) return prev.map((i) => (i.key || i.slug) === key ? { ...i, qty: Math.min(10, i.qty + qty) } : i);
      return [...prev, { key, slug: product.slug, name: product.name, image: product.images[0], price: product.price, qty,
        ...(personalization ? { personalization } : {}) }];
    });
    setCartPing((p) => p + 1);
    toast.success("Added to your cart ✓", { description: personalization ? `${product.name} — personalized for ${personalization.name}` : product.name });
    api.post("/events", { type: "add_to_cart", meta: { slug: product.slug, qty } }).catch(() => {});
  };

  const updateQty = (key, qty) => {
    if (qty <= 0) return setCart((prev) => prev.filter((i) => (i.key || i.slug) !== key));
    setCart((prev) => prev.map((i) => (i.key || i.slug) === key ? { ...i, qty } : i));
  };

  const removeFromCart = (key) => setCart((prev) => prev.filter((i) => (i.key || i.slug) !== key));
  const clearCart = () => setCart([]);

  const toggleWishlist = (slug) => {
    setWishlist((prev) => {
      const has = prev.includes(slug);
      toast.success(has ? "Removed from wishlist" : "Saved to your wishlist");
      return has ? prev.filter((s) => s !== slug) : [...prev, slug];
    });
  };

  const cartCount = cart.reduce((a, i) => a + i.qty, 0);

  return (
    <StoreContext.Provider value={{
      user, setUser, login, register, logout,
      cart, addToCart, updateQty, removeFromCart, clearCart, cartCount,
      wishlist, toggleWishlist, settings, cartOpen, setCartOpen, cartPing,
    }}>
      {children}
    </StoreContext.Provider>
  );
}

export const useStore = () => useContext(StoreContext);
export { formatApiError };
